import { useEffect, useRef, useState } from 'react';
import { BellRing, MessageCircleMore } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { registerAppointmentModificationConfirmHandler } from '@/lib/appointments/appointmentModificationNotification';

export function AppointmentModificationPromptHost() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    registerAppointmentModificationConfirmHandler(async (nextMessage: string) => {
      setMessage(nextMessage);
      setOpen(true);

      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
      });
    });

    return () => {
      registerAppointmentModificationConfirmHandler(null);
    };
  }, []);

  const resolvePrompt = (value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && open) {
          resolvePrompt(false);
        }
      }}
    >
      <AlertDialogContent className="max-w-[560px] gap-5 p-0 overflow-hidden">
        <div className="bg-[linear-gradient(135deg,#165568_0%,#2db1c3_100%)] px-6 py-5 text-white">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/16 ring-1 ring-white/20 backdrop-blur-sm">
              <MessageCircleMore className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.26em] text-white/70">
                Notificación automática
              </div>
              <AlertDialogTitle className="text-left text-[1.45rem] font-semibold tracking-[-0.02em] text-white">
                Avisar cambio de cita
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left text-sm leading-6 text-white/80">
                La cita ya fue actualizada en tu agenda. Decide si quieres notificar al cliente ahora.
              </AlertDialogDescription>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-6 pb-6">
          <div className="rounded-[22px] border border-[#d9edf0] bg-[#f6fbfc] px-4 py-4 text-sm text-[#285766] shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]">
            <div className="flex items-start gap-3">
              <BellRing className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[#2db1c3]" />
              <p className="leading-6">{message}</p>
            </div>
          </div>

          <div className="rounded-[18px] border border-[#efe8d0] bg-[#fffaf0] px-4 py-3 text-xs leading-5 text-[#7a6740]">
            Si eliges enviar, Mestly intentará notificar por los canales activados en la plantilla del tenant.
          </div>

          <AlertDialogFooter className="gap-3 sm:justify-end sm:space-x-0">
            <AlertDialogCancel
              onClick={() => resolvePrompt(false)}
              className="mt-0 rounded-2xl border-[#d1dfe3] px-5 text-[#285766] hover:bg-[#f4fafb]"
            >
              Guardar sin avisar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resolvePrompt(true)}
              className="rounded-2xl bg-[#165568] px-5 text-white shadow-[0_16px_30px_rgba(22,85,104,0.22)] hover:bg-[#114553]"
            >
              Enviar mensaje
            </AlertDialogAction>
          </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
